/**
 * Shopping Carts BFF API response/request types (carts/v1/bff/*)
 */

export type BffMoney = {
  amountInclTax: number;
  amountExclTax: number;
  taxAmount: number;
};

export type BffCartDiscount = {
  name: string;
  amount: number;
};

export type BffCartBundledItem = {
  name: string;
  quantity: number;
  /** Present when the BFF supplies packshot URLs for bundled SKUs. */
  thumbnailUrl?: string;
};

export type BffCartProduct = {
  id: string;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  /** Present on cart line items when the BFF sends catalog limits. */
  maxQuantityPerOrder?: number | null;
  unitPrice: BffMoney;
  quantity: number;
  discounts: BffCartDiscount[];
  totalAmount: BffMoney;
  bundledItems?: BffCartBundledItem[];
};

export type BffCartSubscription = {
  id: string;
  name: string;
  thumbnailUrl?: string;
  description?: string;
  maxQuantityPerOrder?: number | null;
  unitPrice: BffMoney;
  /** Total price for the contracted billing interval when exposed by the carts BFF. */
  intervalPrice?: BffMoney;
  quantity: number;
  discounts: BffCartDiscount[];
  totalAmount: BffMoney;
};

export type BffCartCoupon = {
  code: string;
  name: string;
};

export type BffCartAddress = {
  firstName: string;
  lastName: string;
  email: string;
  addressLine1: string;
  addressLine2?: string;
  postalCode: string;
  houseNumber?: string;
  city: string;
  countryCode: string;
  regionCode?: string;
};

export type BffShoppingCartStatus = 'Pending' | 'Confirmed' | 'Cancelled';

export type BffShoppingCart = {
  id: string;
  status: BffShoppingCartStatus;
  products: BffCartProduct[];
  subscriptions: BffCartSubscription[];
  coupons: BffCartCoupon[];
  subtotal: BffMoney;
  totalDiscounts: number;
  totalShipping: number;
  total: BffMoney;
  currency: string;
  shippingAddress: BffCartAddress | null;
  billingAddress: BffCartAddress | null;
};

export type BffShoppingCartResponse = {
  data: BffShoppingCart;
};

/** Normalizes wire cart payloads (numeric ids, etc.) to {@link BffShoppingCart} shape. */
export function normalizeBffShoppingCartResponse(
  response: BffShoppingCartResponse,
): BffShoppingCartResponse {
  const cart = response.data;
  return {
    data: {
      ...cart,
      products: cart.products.map(p => ({
        ...p,
        id: String(p.id),
      })),
      subscriptions: cart.subscriptions.map(s => ({
        ...s,
        id: String(s.id),
      })),
    },
  };
}

export type BffCreateShoppingCartBody = {
  countryCode: string;
  languageCode: string;
};

/** App layer uses string catalog ids; `fetchers` serializes `id` as a number for the BFF. */
export type BffPutCartProductsBody = {
  products: { id: string; quantity: number }[];
};

/** App layer uses string catalog ids; `fetchers` serializes `id` as a number for the BFF. */
export type BffPutCartSubscriptionsBody = {
  subscriptions: { id: string; quantity: number }[];
};

export type BffPutCartCouponBody = {
  code: string;
};

export type BffCreatePaymentBody = {
  returnUrl: string;
};

export type BffPayment = {
  id: string;
  status: string;
  sessionId: string;
  sessionData: string;
};

export type BffCreatePaymentResponse = {
  data: BffPayment;
};

/** Carts BFF `GET /addresses` suggestion row (matches catalog address suggestion DTO). */
export type BffAddressSuggestion = {
  addressId: string;
  description: string;
};

export type BffFindAddressesResponse = {
  data: BffAddressSuggestion[];
};

/** Carts BFF `GET /addresses/:addressId` detail (matches address suggestion detail DTO). */
export type BffAddressDetail = {
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
  regionCode: string | null;
};

/**
 * Wire JSON for `GET /addresses/:id` may use `addressLine1` (EVA-style) instead of `street`.
 * {@link normalizeBffAddressDetail} maps this into {@link BffAddressDetail}.
 */
export type BffAddressDetailApiRaw = {
  street?: string | null;
  addressLine1?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
  regionCode?: string | null;
};

export function normalizeBffAddressDetail(
  raw: BffAddressDetailApiRaw,
): BffAddressDetail {
  return {
    street: raw.street ?? raw.addressLine1 ?? null,
    houseNumber: raw.houseNumber ?? null,
    postalCode: raw.postalCode ?? null,
    city: raw.city ?? null,
    countryCode: raw.countryCode ?? null,
    regionCode: raw.regionCode ?? null,
  };
}

export type BffGetAddressByReferenceResponse = {
  data: BffAddressDetail;
};

